/**
 * One law.
 *
 * Reading order is what it does, then who put it there, then the fine print.
 * The official title and the section list are real and stay on the page, but
 * they are below the plain-English account rather than above it, because
 * nobody arrives here wanting to parse "Emergency Conservation Program
 * Improvement Act of 2025".
 *
 * The vote is fetched separately from the law. It is a different question,
 * it is slower, and a law with no roll call -- which is more than half of
 * them -- should not delay the half of the page that does exist.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, type Answer, type LawVotes } from "../api";
import { useAsync } from "../useAsync";
import { VoteBar } from "../components/VoteBar";
import { LawNumber, Pill, Problem, Spinner } from "../components/bits";
import { when } from "../format";

export function LawPage() {
  const { billId = "" } = useParams();
  const law = useAsync(() => api.law(billId), [billId]);
  const votes = useAsync(() => api.lawVotes(billId), [billId]);

  if (law.loading) {
    return (
      <div className="mx-auto max-w-3xl px-5">
        <Spinner label="Opening the law…" />
      </div>
    );
  }
  if (law.error || !law.data) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Problem
          error={law.error ?? new Error("That law is not in the corpus.")}
          retry={law.reload}
        />
        <Link to="/laws" className="mt-6 inline-block text-sm font-medium text-brand hover:underline">
          ← Browse every law
        </Link>
      </div>
    );
  }

  const found = law.data;
  const plain = found.plain_title?.trim();
  const points = found.key_points ?? [];

  return (
    <article>
      <header className="border-b border-line bg-gradient-to-b from-brand-soft/50 to-paper">
        <div className="mx-auto max-w-3xl px-5 py-12">
          <div className="flex flex-wrap items-center gap-3">
            <LawNumber value={found.law_number} />
            {found.policy_area && <Pill>{found.policy_area}</Pill>}
            <span className="text-xs text-faint">{when(found.latest_action_date)}</span>
          </div>

          <h1 className="mt-4 font-display text-3xl leading-tight font-semibold text-balance sm:text-4xl">
            {plain || found.title}
          </h1>

          {plain && (
            <p className="mt-4 text-sm leading-relaxed text-muted">
              <span className="font-medium text-ink">Its official title is</span>{" "}
              “{found.official_title || found.title}”. The heading above was written from the
              law's own text to say what it does.
            </p>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-12">
        {points.length > 0 ? (
          <section aria-labelledby="does">
            <h2 id="does" className="font-display text-2xl font-semibold">
              What it does
            </h2>
            <ul className="mt-5 space-y-4">
              {points.map((point, n) => (
                <li key={n} className="flex gap-4">
                  <span className="tnum mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
                    {n + 1}
                  </span>
                  <p className="leading-relaxed">{point}</p>
                </li>
              ))}
            </ul>

            {found.who_it_affects && (
              <div className="mt-8 rounded-2xl border border-line bg-card p-6">
                <h3 className="font-display text-lg font-semibold">Who it affects</h3>
                <p className="mt-2 leading-relaxed text-muted">{found.who_it_affects}</p>
              </div>
            )}

            <p className="mt-6 text-sm text-faint">
              Written by {found.model ?? "a language model"} from{" "}
              {found.grounding === "full text"
                ? "the full filed text of the law"
                : `the ${found.grounding ?? "available text"}`}
              {found.section_count ? `, ${found.section_count} sections` : ""}. Confidence:{" "}
              {found.confidence ?? "unstated"}.
            </p>
          </section>
        ) : (
          <section className="rounded-2xl border border-line bg-card p-6">
            <h2 className="font-display text-xl font-semibold">Not yet summarised</h2>
            <p className="mt-2 text-muted">
              This law has not been through the summariser. Its official title is “
              {found.official_title || found.title}”.
            </p>
          </section>
        )}

        {found.indexed > 0 && <Ask billId={billId} />}

        <section className="mt-14" aria-labelledby="voted">
          <h2 id="voted" className="font-display text-2xl font-semibold">
            Who voted for it
          </h2>
          {votes.loading && <Spinner label="Looking up the roll call…" />}
          {votes.error && <div className="mt-4"><Problem error={votes.error} retry={votes.reload} /></div>}
          {votes.data && <Roll votes={votes.data} />}
        </section>

        {found.sections.length > 0 && (
          <section className="mt-14" aria-labelledby="sections">
            <h2 id="sections" className="font-display text-2xl font-semibold">
              What is in it
            </h2>
            <p className="mt-1.5 text-muted">
              {found.section_count} sections, {found.word_count?.toLocaleString()} words as filed.
            </p>
            <ol className="mt-5 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
              {found.sections.map((section) => (
                <li key={section.ordinal} className="flex gap-4 px-5 py-3">
                  <span className="tnum w-10 shrink-0 text-sm text-faint">{section.number}</span>
                  <span className="flex-1 text-sm">{section.heading || "Untitled section"}</span>
                  <span className="tnum shrink-0 text-xs text-faint">{section.word_count} words</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <p className="mt-14 text-sm text-faint">
          Read the law itself on{" "}
          <a
            className="text-brand hover:underline"
            href={`https://www.congress.gov/bill/${found.congress}th-congress/${
              found.bill_type === "s" ? "senate-bill" : "house-bill"
            }/${found.number}`}
          >
            congress.gov
          </a>
          .
        </p>
      </div>
    </article>
  );
}

/**
 * Ask a question about this one law.
 *
 * Deliberately plain about its own limits. The answer comes only from
 * passages retrieved out of this law's text, so the sections it used are
 * shown underneath — a reader who wants to check has somewhere to look, and
 * one who does not can ignore them. When the retrieved text does not settle
 * the question the server says so, and that is surfaced rather than
 * smoothed over: "the law does not appear to say" is a useful answer about
 * a statute, and a confident guess is a harmful one.
 */
function Ask({ billId }: { billId: string }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const asked = question.trim();
    if (asked.length < 3 || asking) return;
    setAsking(true);
    setProblem(null);
    setAnswer(null);
    try {
      setAnswer(await api.ask(billId, asked));
    } catch (error) {
      // 429 carries wording written for a reader, so it is shown as it
      // came rather than replaced with something generic.
      setProblem(
        error instanceof ApiError ? error.message : "Could not answer that just now.",
      );
    } finally {
      setAsking(false);
    }
  }

  return (
    <section className="mt-14" aria-labelledby="ask">
      <h2 id="ask" className="font-display text-2xl font-semibold">
        Ask about this law
      </h2>
      <p className="mt-1.5 text-muted">
        Answered only from the text of this law — not from the news, and not from what a
        model remembers.
      </p>

      <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          maxLength={400}
          placeholder="Does this change anything for small businesses?"
          aria-label="Your question about this law"
          className="flex-1 rounded-xl border border-line bg-card px-4 py-3 text-base outline-none placeholder:text-faint focus:border-brand"
        />
        <button
          type="submit"
          disabled={asking || question.trim().length < 3}
          className="rounded-xl bg-brand px-5 py-3 font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-40"
        >
          {asking ? "Reading…" : "Ask"}
        </button>
      </form>

      {problem && (
        <p className="mt-4 rounded-xl border border-line bg-sunk/60 px-4 py-3 text-sm text-muted">
          {problem}
        </p>
      )}

      {answer && (
        <div className="mt-5 rounded-2xl border border-line bg-card p-6">
          {!answer.supported && (
            <p className="mb-3 text-sm font-medium text-amber-700">
              The sections found do not settle this.
            </p>
          )}
          <p className="leading-relaxed whitespace-pre-line">{answer.answer}</p>

          {answer.sections_used.length > 0 && (
            <details className="mt-5">
              <summary className="cursor-pointer text-sm text-muted hover:text-ink">
                The {answer.sections_used.length} sections it read
              </summary>
              <ol className="mt-3 space-y-3">
                {answer.sections_used.map((section, index) => (
                  <li key={index} className="border-l-2 border-line pl-4">
                    <p className="text-sm font-medium">
                      {section.number ? `§${section.number} ` : ""}
                      {section.heading || "Untitled section"}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted">{section.excerpt}…</p>
                  </li>
                ))}
              </ol>
            </details>
          )}

          <p className="mt-5 text-xs text-faint">
            Written by {answer.model} from the sections above. Check it against the law
            itself before relying on it.
          </p>
        </div>
      )}
    </section>
  );
}

function Roll({ votes }: { votes: LawVotes }) {
  if (!votes.recorded) {
    return (
      <div className="mt-5 rounded-2xl border border-line bg-sunk/60 p-6">
        <p className="font-medium">Nobody is on record.</p>
        <p className="mt-2 leading-relaxed text-muted">
          This law passed on a voice vote or by unanimous consent, so no individual votes were
          recorded. That is how more than half of all laws pass. It is not the same as everyone
          agreeing — it means no one asked for a count.
        </p>
      </div>
    );
  }

  const chambers = Object.entries(votes.chambers) as [string, NonNullable<LawVotes["chambers"]["house"]>][];

  return (
    <>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {chambers.map(([chamber, vote]) => (
          <div key={chamber} className="rounded-2xl border border-line bg-card p-6">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="font-display text-lg font-semibold capitalize">{chamber}</h3>
              <span className="text-xs text-faint">{when(vote.date)}</span>
            </div>
            <p className="tnum mt-1 text-sm text-muted">
              <strong className="font-semibold text-ink">{vote.yea}</strong> for,{" "}
              <strong className="font-semibold text-ink">{vote.nay}</strong> against — {vote.result}
            </p>
            <div className="mt-5">
              <VoteBar parties={vote.parties} />
            </div>
            <p className="mt-4 text-xs text-faint">On the question: {vote.question}</p>
          </div>
        ))}
      </div>

      {votes.rollcalls > chambers.length && (
        <p className="mt-4 text-sm text-faint">
          There were {votes.rollcalls} roll calls on this bill in total. Shown above is the vote
          on final passage in each chamber — the others were procedural, and their splits often
          run the opposite way.
        </p>
      )}
    </>
  );
}
